<template>
  <el-main>
    <el-container class="user-profile-container">
      <el-row type="flex" class="user-profile" :gutter="32">
        <el-col :md="12" :xs="24">
          <div id="user-data" class="profile-section">
            <div class="outer">
              <h3>{{$t("user_profile.your_profile.title")}}</h3>
              <div>
                <initial-avatar v-bind="$attrs" size="large" :avatarUrl="userData.photo_url" :userObject="userData" />
                <chip @click.native="modals.editProfileModal = true" icon="elsa-icon-edit">{{$t("common.edit")}}</chip>
              </div>
              <info-row :label="$t('user_profile.your_profile.first_name')" :value="userData.first_name" />
              <info-row :label="$t('user_profile.your_profile.last_name')" :value="userData.last_name" />
              <div v-bind:class="{'unconfirmed-email': userHasUnconfirmedEmail}">
                <info-row :label="$t('user_profile.your_profile.email')">
                <p>{{userData.email}}</p>
                </info-row>
                <div v-if="userHasUnconfirmedEmail" class="unconfirmed-email-warning">
                    <i class="warning el-icon-warning-outline"></i>
                    <p class="warning" >{{$t('user_profile.your_profile.not_verified')}}</p>
                    <p>
                      {{$t('user_profile.your_profile.did_not_receive')}}
                      <a>
                        {{$t('user_profile.your_profile.resent_email')}}
                      </a>
                    </p>
                </div>
              </div>
              <info-row
                :label="$t('user_profile.your_profile.user_name')"
                :value="userData.username" />
              <info-row
                :label="$t('user_profile.your_profile.phone_number')" :value="userData.phone_number" />
            </div>
          </div>
          <div id="change-password" class="profile-section">
            <div class="outer">
              <h3>{{$t("user_profile.change_password.title")}}</h3>
              <div>
                <info-row :label="$t('user_profile.change_password.password')" value="******" />
                <chip @click.native="modals.editPasswordModal = true" icon="elsa-icon-edit">{{$t("common.edit")}}</chip>
              </div>
            </div>
          </div>
        </el-col>
        <el-col :md="12" :xs="24" v-if="!isElsaAdmin">
          <div id="your-organizations" class="profile-section">
            <div class="outer">
              <h3>{{$t("user_profile.your_organizations.title")}}</h3>
              <div v-if="!hasOrganizations && !isElsaAdmin" class="no-organizations">
                <p>{{$t("user_profile.dont_belong_organization")}}</p>
                <div class="actions">
                  <div v-if="false" @click="openCreateOrganizationModal">
                    <i class="el-icon-circle-plus-outline" />
                    {{$t("user_profile.create_organization")}}
                  </div>
                </div>
              </div>
              <slot>
                <div class="organization-row" v-for="(org, idx) in organizationData" :key="idx">
                  <img :src="requireOrganizationName" />
                  <div class="organization-row">
                    <router-link
                      :to="{ name: 'organization', params: { organizationId: org.id } }"
                    >
                      <h3 class="organization-name">{{org.name}}</h3>
                    </router-link>
                    <!-- <h3 class="organization-user-position">{{org.department}}, {{org.position}}</h3> -->
                    <role v-for="(role, idx) in userOrganizationData(org.id).roles" :key="idx" :label="role" />
                  </div>
                  <div class="actions-popover">
                    <el-popover
                      placement="bottom-end"
                      width="208"
                      trigger="hover"
                      :visible-arrow="false"
                    >
                      <div class="actions-list">
                        <span @click="handleWithdrawFromOrganizationModal(org.id, userData.id)">{{ $t('user_profile.withdraw_from_organization') }}</span>
                      </div>
                      <el-button slot="reference"><i class="el-icon-more"></i></el-button>
                    </el-popover>
                  </div>
                </div>
              </slot>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-container>

    <div class="user-profile-modals">
      <edit-profile
        :dialogVisible="modals.editProfileModal"
        v-on:closed="modals.editProfileModal = false"
        :userData="userData"
        v-on:emailChanged="modals.confirmAccountModal = $event"
      >
      </edit-profile>
      <change-password
        :dialogVisible="modals.editPasswordModal"
        v-on:closed="modals.editPasswordModal = false"
        :userData="userData"
      >
      </change-password>
      <withdraw-from-organization
        :dialogVisible="modals.withdrawFromOrganizationModal"
        v-on:closed="modals.withdrawFromOrganizationModal = false"
        :organizationId="withdrawOrganization.id"
        :organizationName="withdrawOrganization.name"
        :organizationIsOwner="withdrawOrganization.isOwner"
        :organizationData="withdrawOrganization"
      >
      </withdraw-from-organization>
      <define-password
        :dialogVisible="modals.definePasswordModal"
        v-on:closed="modals.definePasswordModal = false"
        :userData="userData"
      >
      </define-password>
      <confirm-account
        :title="$t('user_profile.your_profile.modals.confirm_new_email.title')"
        :message="$t('user_profile.your_profile.modals.confirm_new_email.message')"
        :buttonLabel="$t('user_profile.your_profile.modals.confirm_new_email.button_label')"
        :dialogVisible="modals.confirmAccountModal"
        v-on:closed="modals.confirmAccountModal = false"
      >
      </confirm-account>
      <delete-account
        :dialogVisible="modals.deleteAccountModal"
        :organizationData="organizationData"
        :userId="userData.id"

        v-on:closed="modals.deleteAccountModal = false"
      >
      </delete-account>
      <create-edit-organization
        :dialogVisible="modals.createOrganizationModal"
        v-on:closed="modals.createOrganizationModal = false"
      />
      <join-organization
        :dialogVisible="modals.joinOrganizationModal"
        v-on:closed="modals.joinOrganizationModal = false"
      />
    </div>
  </el-main>
</template>

<script>
  import Chip from '@/components/common/Chip'
  import ChangePassword from '@/components/modals/user-profile/ChangePassword'
  import ConfirmAccount from '@/components/modals/confirm-account/ConfirmAccount'
  import DefinePassword from '@/components/modals/user-profile/DefinePassword'
  import DeleteAccount from '@/components/modals/user-profile/DeleteAccount'
  import EditProfile from '@/components/modals/user-profile/EditProfile'
  import WithdrawFromOrganization from '@/components/modals/user-profile/WithdrawFromOrganization'
  import Role from '@/components/common/Role'
  import InfoRow from '@/components/common/InfoRow'
  import CreateEditOrganization from '@/components/modals/organizations/CreateEditOrganization'
  import JoinOrganization from '@/components/modals/organizations/JoinOrganization'
  import InitialAvatar from '@/components/common/InitialAvatar'
  import {
    pathOr,
    propEq,
    find,
    compose,
    join,
    prepend,
    splitEvery,
    length,
    propOr,
  } from 'ramda'
  import { mapGetters } from 'vuex'


  export default {
    name: "UserProfile",
    components: {
      ChangePassword,
      Chip,
      ConfirmAccount,
      DefinePassword,
      DeleteAccount,
      EditProfile,
      WithdrawFromOrganization,
      Role,
      InfoRow,
      CreateEditOrganization,
      JoinOrganization,
      InitialAvatar
    },
    async mounted(){
      this.$store.dispatch('organization/loadUserOrganizationsData')
    },
    data () {
      return {
        withdrawOrganization: {},
        modals: {
          createOrganizationModal: false,
          editProfileModal: false,
          editPasswordModal: false,
          withdrawFromOrganizationModal: false,
          definePasswordModal: false,
          confirmAccountModal: false,
          deleteAccountModal: false,
          joinOrganizationModal: false
        },
        social: {
          googleLogin: false,
          facebookLogin: false,
        }
      }
    },
    methods: {
      buildClassForRole(role) { return `organization-user-roles-${role}` },
      async handleWithdrawFromOrganizationModal(organizationId, userId) {
        const organization = find(propEq('id', organizationId), this.organizationData)
        this.withdrawOrganization = { ...organization, userId };
        this.modals.withdrawFromOrganizationModal = true;
      },
      openCreateOrganizationModal() {
        this.modals.createOrganizationModal = true
      },
      async usersRoles(organizationId, userId) {
        const user = await this.$store.dispatch('organization/getOrganizationUser', {
          organizationId,
          userId,
        })

        return propOr([], 'roles')(user)
      }
    },
    computed: {
      requireOrganizationName () {
        try {
          return require(`@/assets/images/${this.organizationData[0].image}`)
        }
        catch {
          return require('@/assets/images/ic.svg')
        }
      },
      userHasUnconfirmedEmail(){
        return this.userData.unconfirmedEmail;
      },
      userData: pathOr({}, ['$store','state','user', 'userInfo']),
      organizationData() {
        return this.$store.getters['organization/userOrganizations']
      },
      hasOrganizations () {
        return length(this.organizationData) > 0
      },
      getCompletePhoneNumber () {
        const phoneCode = pathOr('', ['userData', 'phoneCode'])(this)
        const phoneNumber = compose(
          splitEvery(3),
          pathOr('', ['userData', 'phoneNumber'])
        )(this)

        return compose(
          join(' '),
          prepend(phoneCode)
          )(phoneNumber)
      },
      ...mapGetters('user', [
        'isElsaAdmin'
      ]),
      ...mapGetters('organization', [
        'userOrganizationsData',
        'userOrganizationData',
        'isOrganizationAdmin',
        'isOrganizationAssociateAdmin',
      ])
    },
  }
</script>

<style lang="scss" scoped>
  @import '../scss/colors';

  .actions-popover {
    display: flex;
    flex: 1;
    justify-content: flex-end;
  }

</style>
